import { Block, BLOCKS, Document, TopLevelBlock } from '@contentful/rich-text-types';
import { documentToReactComponents, Options } from '@contentful/rich-text-react-renderer';
import { cn } from 'utils/styles';

/**
 * Extracts the plain text content from a table cell block.
 */
function getCellText(cell: Block): string {
  const parts: string[] = [];

  function walk(node: { nodeType: string; value?: string; content?: unknown[] }) {
    if (node.nodeType === 'text' && typeof node.value === 'string') {
      parts.push(node.value);
    }
    if (Array.isArray(node.content)) {
      for (const child of node.content) {
        walk(child as { nodeType: string; value?: string; content?: unknown[] });
      }
    }
  }

  walk(cell);
  return parts.join('').trim();
}

/**
 * Renders the rich text content of a table cell using documentToReactComponents.
 */
function renderCellContent(cell: Block, options?: Options): React.ReactNode {
  const doc: Document = {
    nodeType: BLOCKS.DOCUMENT,
    data: {},
    content: (cell.content ?? []) as TopLevelBlock[],
  };
  return documentToReactComponents(doc, options);
}

interface MergedCell {
  cell: Block;
  colspan: number;
}

/**
 * Processes a row's cells, merging any cell whose text is exactly "^" into the
 * preceding cell by incrementing its colspan.
 */
function mergeRowCells(cells: Block[]): MergedCell[] {
  const merged: MergedCell[] = [];

  for (const cell of cells) {
    if (getCellText(cell) === '^' && merged.length > 0) {
      merged[merged.length - 1].colspan += 1;
    } else {
      merged.push({ cell, colspan: 1 });
    }
  }

  return merged;
}

interface AgreementTableProps {
  node: Block;
  renderOptions?: Options;
  /**
   * Enables the GLBA model-form shading (see `shadeMode`). Set per page via the
   * `tableLayout` field (see AgreementPageTemplate); every other agreement page
   * renders with the default column dividers and no shading.
   */
  banded?: boolean;
}

const isHeaderRow = (row: Block) => {
  const cells = (row.content ?? []) as Block[];
  return cells.length > 0 && cells[0].nodeType === 'table-header-cell';
};

/**
 * Decides which cells a banded (GLBA model-form) table shades nickel, from the
 * table's own shape — no per-cell authoring required:
 *  - `band`      a table containing a full-span section-header row ("Who we
 *                are", "What we do", …) shades only those band rows.
 *  - `headerRow` the wide comparison grid ("Reasons We Can Share…") shades its
 *                header row across all columns.
 *  - `firstCol`  a plain two-column key/value table (FACTS, "To Limit Our
 *                Sharing") shades its left label column.
 */
type ShadeMode = 'band' | 'headerRow' | 'firstCol';

function getShadeMode(mergedRows: MergedCell[][]): ShadeMode {
  const hasBand = mergedRows.some(cells => cells.length === 1 && cells[0].colspan > 1);
  if (hasBand) return 'band';
  const maxCols = mergedRows.reduce((max, cells) => Math.max(max, cells.length), 0);
  return maxCols >= 3 ? 'headerRow' : 'firstCol';
}

/**
 * Column widths for banded (GLBA model-form) tables, as percentages of the
 * table width — reproducing the model-form proportions from the design at both
 * breakpoints (765px desktop / 342px mobile) instead of letting content size the
 * columns. Consumed as a `<colgroup>` on a `table-fixed` table.
 *  - `firstCol`  desktop-only (stacks on mobile), so no responsive variant.
 *  - `headerRow` 3-col comparison grid; slightly wider label column on mobile.
 *  - `band`      2-col section rows; even split on mobile, 41/59 on desktop.
 */
function bandedColWidths(shadeMode: ShadeMode): string[] {
  switch (shadeMode) {
    case 'firstCol':
      return ['w-[26.3%]', 'w-[73.7%]'];
    case 'headerRow':
      return ['w-[53.2%] md:w-[47.4%]', 'w-[23.4%] md:w-[26.3%]', 'w-[23.4%] md:w-[26.3%]'];
    case 'band':
      return ['w-1/2 md:w-[41%]', 'w-1/2 md:w-[59%]'];
  }
}

/**
 * Desktop table view. Renders inner borders only (between rows and columns,
 * no outer border) with colspan merging via `^`. When `banded`, drops the
 * column dividers and applies GLBA model-form nickel shading (see getShadeMode).
 */
function DesktopTable({
  rows,
  renderOptions,
  hiddenOnMobile,
  noMargin,
  banded,
}: {
  rows: Block[];
  renderOptions?: Options;
  hiddenOnMobile?: boolean;
  noMargin?: boolean;
  banded?: boolean;
}) {
  const mergedRows = rows.map(row => mergeRowCells((row.content ?? []) as Block[]));
  const shadeMode = banded ? getShadeMode(mergedRows) : undefined;
  const colWidths = shadeMode ? bandedColWidths(shadeMode) : undefined;

  return (
    <table
      className={cn(
        'w-full table-fixed text-left border-collapse font-sans',
        !noMargin && 'my-12',
        hiddenOnMobile && 'hidden md:table',
      )}>
      {colWidths && (
        <colgroup>
          {colWidths.map((width, i) => (
            <col key={i} className={width} />
          ))}
        </colgroup>
      )}
      <tbody>
        {rows.map((row, rowIndex) => {
          const mergedCells = mergedRows[rowIndex];
          const isHeader = isHeaderRow(row);
          const isLastRow = rowIndex === rows.length - 1;

          return (
            <tr
              key={rowIndex}
              className={cn(
                isHeader ? 'text-kale' : 'text-content-secondary',
                // Banded (model-form) tables close with a bottom divider under
                // the last row; other agreement tables only rule between rows.
                (banded || !isLastRow) && 'border-b border-pebble',
              )}>
              {mergedCells.map((merged, cellIndex) => {
                const content = renderCellContent(merged.cell, renderOptions);
                const isTh = merged.cell.nodeType === 'table-header-cell';
                const colSpan = merged.colspan > 1 ? merged.colspan : undefined;
                const isFullSpan = mergedCells.length === 1 && merged.colspan > 1;

                let extra: string;
                if (banded) {
                  const shaded =
                    shadeMode === 'band' ? isFullSpan : shadeMode === 'headerRow' ? isHeader : cellIndex === 0;
                  extra = shaded ? 'bg-nickel' : '';
                } else {
                  const isLastCell = cellIndex === mergedCells.length - 1;
                  extra = cn(!isLastCell && 'border-r border-pebble', isFullSpan && 'text-center');
                }

                if (isTh) {
                  return (
                    <th key={cellIndex} colSpan={colSpan} className={cn('font-medium px-4 py-6 align-top', extra)}>
                      {content}
                    </th>
                  );
                }

                return (
                  <td key={cellIndex} colSpan={colSpan} className={cn('px-4 py-6 align-top', extra)}>
                    {content}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/**
 * Returns true if a row contains any `^` merge markers.
 */
function rowHasMerge(row: Block): boolean {
  const cells = (row.content ?? []) as Block[];
  return cells.some(cell => getCellText(cell) === '^');
}

/**
 * Mobile transposed table. Each data row's cells are paired with their
 * column header in a two-column layout.
 *
 * Desktop:                    Mobile:
 * Header A | Header B         Header A | copy1
 * copy1    | copy2            Header B | copy2
 * copy3    | copy4            Header A | copy3
 *                             Header B | copy4
 */
function MobileTransposedTable({
  headerCells,
  dataRows,
  renderOptions,
  isLastSection,
}: {
  headerCells: Block[];
  dataRows: Block[];
  renderOptions?: Options;
  isLastSection: boolean;
}) {
  return (
    <table className="w-full text-left border-collapse font-sans">
      <tbody>
        {dataRows.map((row, rowIndex) => {
          const cells = (row.content ?? []) as Block[];
          const isLastRow = isLastSection && rowIndex === dataRows.length - 1;

          return cells.map((cell, cellIndex) => {
            const header = headerCells[cellIndex];
            const isLastCell = cellIndex === cells.length - 1;
            const showBorder = !(isLastRow && isLastCell);

            return (
              <tr key={`${rowIndex}-${cellIndex}`} className={showBorder ? 'border-b border-pebble' : ''}>
                <th className="w-1/2 text-kale font-medium px-4 py-6 align-top border-r border-pebble">
                  {header ? renderCellContent(header, renderOptions) : null}
                </th>
                <td className="w-1/2 text-content-secondary px-4 py-6 align-top">
                  {renderCellContent(cell, renderOptions)}
                </td>
              </tr>
            );
          });
        })}
      </tbody>
    </table>
  );
}

/**
 * Mobile layout for banded two-column key/value tables (FACTS, "To Limit Our
 * Sharing"). A 342px viewport can't fit the label column beside its value, so
 * each row stacks: a full-width shaded label band above its full-width value,
 * matching the GLBA model-form mobile design. (The comparison grid and the
 * section tables keep their desktop layout on mobile, so they don't come here.)
 */
function StackedMobileTable({ rows, renderOptions }: { rows: Block[]; renderOptions?: Options }) {
  return (
    <table className="w-full table-auto text-left border-collapse font-sans my-12 md:hidden">
      <tbody>
        {rows.map((row, rowIndex) => {
          const cells = (row.content ?? []) as Block[];
          const isHeader = isHeaderRow(row);
          const rowText = isHeader ? 'text-kale' : 'text-content-secondary';

          return cells.map((cell, cellIndex) => {
            const isTh = cell.nodeType === 'table-header-cell';
            const isLabel = cellIndex === 0;
            const isLastCell = cellIndex === cells.length - 1;
            // Rule after each label/value group, including a closing divider at
            // the bottom of the table (matches the model-form layout).
            const border = isLastCell ? 'border-b border-pebble' : '';
            const className = cn('px-4 py-6 align-top', rowText, border, isLabel && 'bg-nickel', isTh && 'font-medium');
            const content = renderCellContent(cell, renderOptions);

            return (
              <tr key={`${rowIndex}-${cellIndex}`}>
                {isTh ? <th className={className}>{content}</th> : <td className={className}>{content}</td>}
              </tr>
            );
          });
        })}
      </tbody>
    </table>
  );
}

/**
 * Table component for agreement pages. Renders a plain HTML table with support
 * for colspan merging via the `^` marker convention. When a cell contains only
 * the text "^", it is merged into the cell to its left.
 *
 * On mobile, tables with an unmerged header row transpose: each data cell is
 * paired with its column header. Transposition stops at the first data row
 * that contains merge markers — that row and all subsequent rows render as
 * a normal (non-transposed) table.
 */
const AgreementTable = ({ node, renderOptions, banded }: AgreementTableProps) => {
  const rows = (node.content ?? []) as Block[];

  // Agreement pages migrated from WordPress use 1×1 tables (role="presentation")
  // as styled callout boxes. Render those as a bordered div instead of a table.
  if (rows.length === 1) {
    const cells = (rows[0].content ?? []) as Block[];
    if (cells.length === 1) {
      return (
        <div className="my-12 rounded-3xl border border-kale p-6 font-sans text-content-secondary">
          {renderCellContent(cells[0], renderOptions)}
        </div>
      );
    }
  }

  // Banded (GLBA model-form) pages don't use the header-paired mobile transpose.
  // Per the model-form mobile design: two-column key/value tables (FACTS, "To
  // Limit Our Sharing") stack label-over-value; the comparison grid and the
  // section tables keep their desktop layout on mobile (just narrower).
  if (banded) {
    const mergedRows = rows.map(row => mergeRowCells((row.content ?? []) as Block[]));
    if (getShadeMode(mergedRows) === 'firstCol') {
      return (
        <>
          <DesktopTable rows={rows} renderOptions={renderOptions} hiddenOnMobile banded />
          <StackedMobileTable rows={rows} renderOptions={renderOptions} />
        </>
      );
    }
    return <DesktopTable rows={rows} renderOptions={renderOptions} banded />;
  }

  const hasHeaderRow = rows.length > 0 && ((rows[0].content ?? []) as Block[])[0]?.nodeType === 'table-header-cell';
  const headerHasMerge = hasHeaderRow && rowHasMerge(rows[0]);

  // Only transpose if there's an unmerged header row with data rows
  if (!hasHeaderRow || headerHasMerge || rows.length <= 1) {
    return <DesktopTable rows={rows} renderOptions={renderOptions} />;
  }

  // Split data rows into transposable (no merges) and remaining (from first merge onward)
  const dataRows = rows.slice(1);
  let splitIndex = dataRows.length;
  for (let i = 0; i < dataRows.length; i++) {
    if (rowHasMerge(dataRows[i])) {
      splitIndex = i;
      break;
    }
  }

  const transposableRows = dataRows.slice(0, splitIndex);
  const remainingRows = dataRows.slice(splitIndex);
  const headerCells = (rows[0].content ?? []) as Block[];

  return (
    <>
      <DesktopTable rows={rows} renderOptions={renderOptions} hiddenOnMobile />
      {(transposableRows.length > 0 || remainingRows.length > 0) && (
        <div className="my-12 md:hidden">
          {transposableRows.length > 0 && (
            <MobileTransposedTable
              headerCells={headerCells}
              dataRows={transposableRows}
              renderOptions={renderOptions}
              isLastSection={remainingRows.length === 0}
            />
          )}
          {remainingRows.length > 0 && <DesktopTable rows={remainingRows} renderOptions={renderOptions} noMargin />}
        </div>
      )}
    </>
  );
};

export { AgreementTable };
